import React, { useEffect, useState } from 'react';
import { atlasClient } from '../../../../api/atlasClient';
import { SparkLibrarySearchHit, SparkCreative } from '../../types/sparkTypes';
import { CreativeCard } from '../CreativeCard';
import boardStyles from '../../styles/CreativeBoard.module.css';
import cardStyles from '../../styles/CreativeCard.module.css';
import { CreativeListItem } from './CreativeListItem';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CircularProgress from '@mui/material/CircularProgress';
import emptyStyles from '../../styles/InboxView.module.css';

/** Loading indicator that only appears after a short delay to avoid flicker. */
export const DelayedLoader: React.FC<{ visible: boolean; delay?: number }> = ({ visible, delay = 300 }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!visible) { setShow(false); return; }
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [visible, delay]);
  if (!show) return null;
  return (
    <div className={emptyStyles.emptyState} style={{ padding: '48px 24px' }}>
      <div className={emptyStyles.emptyStateIcon}>
        <CircularProgress size={24} style={{ color: '#b0b8c1' }} />
      </div>
      <p className={emptyStyles.emptyStateDescription}>Searching…</p>
    </div>
  );
};

type ViewMode = 'grid-large' | 'grid-small' | 'list';

export const Results: React.FC<{
  items: SparkLibrarySearchHit[];
  viewMode: ViewMode;
  onOpen: (snapshotFqn: string) => void;
  // Optional multi-select support
  selectable?: boolean;
  selectedFqns?: string[];
  onToggleSelect?: (fqn: string, selected: boolean) => void;
  // Show published badge (useful for "My Items" where both drafts and published coexist)
  showPublishedBadge?: boolean;
  // Whether any search query or filters are active (affects empty state message)
  hasActiveFilters?: boolean;
}> = ({ items, viewMode, onOpen, selectable = false, selectedFqns = [], onToggleSelect, showPublishedBadge = false, hasActiveFilters = false }) => {
  if (!items || items.length === 0) {
    return (
      <div className={emptyStyles.emptyState}>
        <div className={emptyStyles.emptyStateIcon}>
          <SearchOffIcon />
        </div>
        <h3 className={emptyStyles.emptyStateTitle}>
          {hasActiveFilters ? 'No results found' : 'No items published yet'}
        </h3>
        <p className={emptyStyles.emptyStateDescription}>
          {hasActiveFilters
            ? 'Try adjusting your search terms or filters to find what you\u2019re looking for.'
            : 'Published items will appear here once they become available.'}
        </p>
      </div>
    );
  }

  const toCreative = (hit: SparkLibrarySearchHit): SparkCreative => ({
    fqn: hit.fqn,
    _project_fqn: '',
    _creative_type_fqn: hit.creative_type_fqn,
    name: hit.title,
    description: hit.description,
    _labels: [],
    _iteration_fqns: [],
    _active_iteration_fqn: null,
    _active_jobs: 0,
    _successful_jobs: 0,
    _failed_jobs: 0,
    _updated_at: null,
    _changed_at: null,
    _created_at: null,
    _active_iteration_number: null,
    _hero_image_id: null,
    _attachment_ids: [],
    _attachment_count: 0,
    ancestor_creative_fqns: [],
    tag_pattern: hit.tag_pattern,
    is_published: true,
    published_at: hit.published_at,
    published_by: hit.published_by,
    published_snapshot_fqn: hit.fqn,
  });

  if (viewMode === 'list') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
        {items.map((hit) => (
          <CreativeListItem
            key={hit.fqn}
            fqn={hit.fqn}
            title={hit.title}
            description={hit.description}
            thumbnailUrl={hit.thumbnail_file_id ? atlasClient.getAssetUrl(hit.thumbnail_file_id) : undefined}
            tagPattern={hit.tag_pattern}
            publishedAt={hit.state === 'draft' ? hit.last_modified_at : hit.published_at}
            publishedBy={hit.published_by}
            state={hit.state}
            isPublished={showPublishedBadge ? hit.state === 'published' : undefined}
            onClick={() => onOpen(hit.fqn)}
            selectable={selectable}
            selected={selectedFqns.includes(hit.fqn)}
            onToggleSelected={(checked) => onToggleSelect && onToggleSelect(hit.fqn, checked)}
          />
        ))}
      </div>
    );
  }

  const size = viewMode === 'grid-small' ? 'small' : 'large';
  const formatDate = (iso?: string): string | undefined => {
    if (!iso) return undefined;
    try {
      return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' as const });
    } catch {
      return new Date(iso).toLocaleDateString();
    }
  };
  return (
    <ul
      className={`${boardStyles.creativesContainer} ${size === 'small' ? boardStyles.smallGrid : boardStyles.largeGrid}`}
      style={{ overflowY: 'visible' }}
    >
      {items.map((hit) => (
        <li key={hit.fqn} className={`${boardStyles.cardWrapper} ${selectable && selectedFqns.includes(hit.fqn) ? boardStyles.selected : ''}`}>
          {selectable && (
            <div className={boardStyles.selectionOverlay}>
              <input
                type="checkbox"
                className={boardStyles.selectionCheckbox}
                checked={selectedFqns.includes(hit.fqn)}
                onChange={(e) => onToggleSelect && onToggleSelect(hit.fqn, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                aria-label={selectedFqns.includes(hit.fqn) ? 'Deselect' : 'Select'}
              />
            </div>
          )}
          <CreativeCard
            creative={toCreative(hit)}
            size={size as 'small' | 'large'}
            onClick={() => onOpen(hit.fqn)}
            thumbnailUrl={hit.thumbnail_file_id
              ? (size === 'large'
                  ? atlasClient.getAssetUrl(hit.thumbnail_file_id, { full: true })
                  : atlasClient.getAssetUrl(hit.thumbnail_file_id))
              : undefined}
            hidePublishedBadge={!showPublishedBadge}
            hideTypeBadge={true}
            typeBadgeOverride={(() => {
              const displayDate = formatDate(
                (hit.state === 'draft' ? hit.last_modified_at : hit.published_at) || undefined
              );
              const hasDate = !!displayDate;
              return (
                <>
                  {hasDate && <span className={cardStyles.typeBadge}>{displayDate}</span>}
                  {hit.state === 'draft' && (
                    <span className={cardStyles.draftBadge} style={hasDate ? { marginLeft: 8 } : undefined}>Draft</span>
                  )}
                </>
              );
            })()}
          />
        </li>
      ))}
    </ul>
  );
};
